// src/components/bulletin/PublicCasefiles.js

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc } from 'firebase/firestore';
import { FaVideo, FaRobot, FaClock, FaEye, FaChevronRight, FaChevronDown, FaPlay, FaShareAlt, FaChartBar } from 'react-icons/fa';

const PublicCasefiles = () => {
  const [publicFiles, setPublicFiles] = useState([]);
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [userDisplayNames, setUserDisplayNames] = useState({});
  const [expandedMap, setExpandedMap] = useState({});
  const location = useLocation();

  // Extract search term from URL params
  const searchParams = new URLSearchParams(location.search);
  const searchTerm = searchParams.get('search') || '';

  useEffect(() => {
    const q = query(
      collection(db, 'casefiles'),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const files = [];
      const creatorIds = new Set();
      
      querySnapshot.forEach((doc) => {
        const fileData = doc.data();
        files.push({ id: doc.id, ...fileData });
        
        // Collect unique creator IDs to fetch display names
        if (fileData.createdBy) {
          creatorIds.add(fileData.createdBy);
        }
      });
      
      setPublicFiles(files);
      
      // We now need to wait for state update, so we'll use files directly
      // instead of relying on the publicFiles state variable
      fetchUserDisplayNames(Array.from(creatorIds), files);
      
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching public casefiles:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);
  
  // Function to fetch display names for all creators
  const fetchUserDisplayNames = async (userIds, filesData) => {
    try {
      const names = {};
      
      for (const userId of userIds) {
        try {
          const userDoc = await getDoc(doc(db, 'userProfiles', userId));
          
          // Find the corresponding file to get the email from the passed filesData
          const fileWithUserId = filesData.find(file => file.createdBy === userId);
          const userEmail = fileWithUserId?.creatorEmail || 'unknown@email.com';
          
          if (userDoc.exists() && userDoc.data().displayName) {
            // If user has explicitly set a display name, use it
            names[userId] = userDoc.data().displayName;
          } else {
            // Otherwise use their email address as the display name
            names[userId] = userEmail;
          }
        } catch (err) {
          console.error(`Error fetching user ${userId}:`, err);
          
          // Find the email as fallback
          const fileWithUserId = filesData.find(file => file.createdBy === userId);
          names[userId] = fileWithUserId?.creatorEmail || 'Unknown User';
        }
      }
      
      setUserDisplayNames(names);
    } catch (error) {
      console.error("Error fetching user display names:", error);
    }
  };

  // Fetch evidence counts for all public casefiles
  useEffect(() => {
    if (publicFiles.length === 0) return;

    // Temporary: Use simple collection query while indexes are building
    const evidenceQuery = query(
      collection(db, 'evidence'),
      where('casefileId', 'in', publicFiles.map(file => file.id))
    );

    const unsubscribe = onSnapshot(evidenceQuery, (querySnapshot) => {
      const counts = {};
      const evidenceData = {};
      
      // Initialize counts for all casefiles
      publicFiles.forEach(file => {
        counts[file.id] = {
          totalEvidence: 0,
          analyzedEvidence: 0,
          recentActivity: 0,
          totalVehicles: 0,
          totalPlates: 0
        };
        evidenceData[file.id] = [];
      });

      // Process evidence documents
      querySnapshot.forEach((doc) => {
        const evidence = { id: doc.id, ...doc.data() };
        const casefileId = evidence.casefileId;
        
        if (counts[casefileId]) {
          evidenceData[casefileId].push(evidence);
          counts[casefileId].totalEvidence++;
          
          // Check if analyzed
          if (evidence.analysis && evidence.analysis.trim() !== '') {
            counts[casefileId].analyzedEvidence++;
            
            // Count vehicles and plates from AI analysis
            const analysisText = evidence.analysis;
            if (!analysisText.toLowerCase().includes('error:') && 
                analysisText.trim().toLowerCase() !== 'n/a') {
              
              // Try to parse JSON first
              try {
                const jsonMatch = analysisText.match(/\[.*\]|\{.*\}/s);
                if (jsonMatch) {
                  const jsonData = JSON.parse(jsonMatch[0]);
                  if (Array.isArray(jsonData)) {
                    counts[casefileId].totalVehicles += jsonData.length;
                    
                    const platesInEvidence = jsonData.filter(item => 
                      item["License Plate Number"] && 
                      item["License Plate Number"] !== 'Not detected' &&
                      item["License Plate Number"].length >= 2
                    ).length;
                    counts[casefileId].totalPlates += platesInEvidence;
                  }
                }
              } catch (e) {
                // Fallback to text parsing if JSON fails
                const quotedDescriptions = analysisText.match(/"([^"]+)"/g);
                if (quotedDescriptions) {
                  const vehicleCount = quotedDescriptions.filter(desc => {
                    const cleanDesc = desc.replace(/"/g, '').toLowerCase();
                    return cleanDesc.includes('car') || cleanDesc.includes('suv') || 
                           cleanDesc.includes('truck') || cleanDesc.includes('sedan') || 
                           cleanDesc.includes('hatchback');
                  }).length;
                  counts[casefileId].totalVehicles += vehicleCount;
                }
              }
            }
          }
          
          // Check if recent (last 7 days)
          if (evidence.createdAt) {
            const evidenceDate = evidence.createdAt.toDate ? evidence.createdAt.toDate() : new Date(evidence.createdAt);
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            if (evidenceDate >= weekAgo) {
              counts[casefileId].recentActivity++;
            }
          }
        }
      });

      setEvidenceCounts(counts);
    }, (error) => {
      console.error("Error fetching evidence counts:", error);
    });

    return () => unsubscribe();
  }, [publicFiles]);

  // Extract all unique categories from casefiles
  const availableCategories = useMemo(() => {
    const categories = new Set();
    publicFiles.forEach(file => {
      if (file.categories && Array.isArray(file.categories)) {
        file.categories.forEach(category => categories.add(category));
      }
    });
    return Array.from(categories).sort();
  }, [publicFiles]);

  // Filter casefiles based on search term and selected category
  const filteredFiles = useMemo(() => {
    let filtered = publicFiles;

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(file => {
        const titleMatch = file.title?.toLowerCase().includes(lowerSearchTerm);
        const descriptionMatch = file.description?.toLowerCase().includes(lowerSearchTerm);
        const categoryMatch = file.categories?.some(category => 
          category.toLowerCase().includes(lowerSearchTerm)
        );
        const emailMatch = file.creatorEmail?.toLowerCase().includes(lowerSearchTerm);
        
        return titleMatch || descriptionMatch || categoryMatch || emailMatch;
      });
    }

    // Apply category filter
    if (selectedCategory && selectedCategory !== 'All') {
      filtered = filtered.filter(file => 
        file.categories && file.categories.includes(selectedCategory)
      );
    }

    return filtered;
  }, [publicFiles, searchTerm, selectedCategory]);

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
  };

  const toggleExpanded = (caseId) => {
    setExpandedMap((prev) => ({ ...prev, [caseId]: !prev[caseId] }));
  };

  // Count files per category for display
  const getCategoryCount = (category) => {
    if (category === 'All') return publicFiles.length;
    return publicFiles.filter(file => 
      file.categories && file.categories.includes(category)
    ).length;
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: 'auto' }}>
      <h1>Public Casefiles</h1>
      
      {searchTerm && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '10px', 
          backgroundColor: '#1a1a1a', 
          borderRadius: '5px',
          border: '1px solid #333'
        }}>
          <p style={{ margin: 0, color: '#ccc' }}>
            🔍 Search results for: <strong>"{searchTerm}"</strong>
            {filteredFiles.length > 0 && (
              <span> ({filteredFiles.length} result{filteredFiles.length !== 1 ? 's' : ''})</span>
            )}
          </p>
        </div>
      )}

      {/* Category Filter Section */}
      {availableCategories.length > 0 && (
        <div style={{ 
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#1a1a1a',
          borderRadius: '8px',
          border: '1px solid #333'
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            fontSize: '1.1em', 
            color: '#fff',
            display: 'flex',
            alignItems: 'center'
          }}>
            🏷️ Filter by Category
          </h3>
          
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '8px',
            alignItems: 'center'
          }}>
            {/* All Categories Button */}
            <button
              onClick={() => handleCategoryFilter('All')}
                              style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #333',
                  backgroundColor: selectedCategory === 'All' ? '#E53935' : '#2a2a2a',
                  color: selectedCategory === 'All' ? '#fff' : '#ccc',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory !== 'All') {
                    e.target.style.backgroundColor = '#333';
                    e.target.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory !== 'All') {
                    e.target.style.backgroundColor = '#2a2a2a';
                    e.target.style.color = '#ccc';
                  }
                }}
            >
              All Categories
              <span style={{
                backgroundColor: selectedCategory === 'All' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '0.8em'
              }}>
                {getCategoryCount('All')}
              </span>
            </button>

            {/* Individual Category Buttons */}
            {availableCategories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryFilter(category)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #333',
                  backgroundColor: selectedCategory === category ? '#E53935' : '#2a2a2a',
                  color: selectedCategory === category ? '#fff' : '#ccc',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory !== category) {
                    e.target.style.backgroundColor = '#333';
                    e.target.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory !== category) {
                    e.target.style.backgroundColor = '#2a2a2a';
                    e.target.style.color = '#ccc';
                  }
                }}
              >
                {category}
                <span style={{
                  backgroundColor: selectedCategory === category ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '0.8em'
                }}>
                  {getCategoryCount(category)}
                </span>
              </button>
            ))}
          </div>
          
          {/* Active filters display */}
          {(searchTerm || selectedCategory !== 'All') && (
            <div style={{ marginTop: '12px', fontSize: '0.9em', color: '#aaa' }}>
              Active filters: 
              {searchTerm && <span style={{ marginLeft: '8px', color: '#76a9ea' }}>Search: "{searchTerm}"</span>}
              {selectedCategory !== 'All' && <span style={{ marginLeft: '8px', color: '#E53935' }}>Category: {selectedCategory}</span>}
            </div>
          )}
        </div>
      )}

      <p>These cases have been made public for community analysis and contribution.</p>
      <hr style={{ margin: '20px 0', border: '1px solid #444' }} />
      
      {isLoading && <p>Loading public cases...</p>}
      
      {!isLoading && filteredFiles.length === 0 && (
        <p>
          {searchTerm || selectedCategory !== 'All'
            ? `No public casefiles found matching your filters.` 
            : "There are currently no public casefiles."
          }
          {(searchTerm || selectedCategory !== 'All') && (
            <button
              onClick={() => {
                setSelectedCategory('All');
                // Clear search if needed (would require more complex state management)
              }}
              style={{
                marginLeft: '10px',
                padding: '4px 8px',
                backgroundColor: 'transparent',
                border: '1px solid #E53935',
                color: '#E53935',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9em'
              }}
            >
              Clear Filters
            </button>
          )}
        </p>
      )}
      
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {filteredFiles.map((file) => {
          const counts = evidenceCounts[file.id] || {
            totalEvidence: 0,
            analyzedEvidence: 0,
            recentActivity: 0,
            totalVehicles: 0,
            totalPlates: 0
          };
          
          // Get creator display name
          const creatorDisplayName = file.createdBy ? userDisplayNames[file.createdBy] : 'Unknown User';
          const isExpanded = !!expandedMap[file.id];

          
          return (
            <li key={file.id} style={{ 
              background: '#1a1a1a', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '12px',
              border: (searchTerm || selectedCategory !== 'All') ? '1px solid #333' : 'none',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <button
                  onClick={() => toggleExpanded(file.id)}
                  aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ccc',
                    cursor: 'pointer',
                    padding: 0,
                    marginRight: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                </button>
                <Link to={`/casefile/${file.id}`} style={{ 
                  color: '#76a9ea', 
                  textDecoration: 'none', 
                  fontSize: '1.2em',
                  fontWeight: '500',
                  flex: 1
                }}>
                  {file.title}
                </Link>
                
                {/* Evidence Activity Indicators */}
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  alignItems: 'center',
                  marginLeft: '15px'
                }}>
                  {counts.recentActivity > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#2d5016',
                      borderRadius: '12px',
                      fontSize: '0.8em',
                      color: '#4CAF50',
                      border: '1px solid #4CAF50'
                    }}>
                      <FaClock style={{ fontSize: '0.9em' }} />
                      Active
                    </div>
                  )}
                  
                  {counts.totalPlates > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#1a2332',
                      borderRadius: '12px',
                      fontSize: '0.8em',
                      color: '#76a9ea',
                      border: '1px solid #76a9ea'
                    }}>
                      <FaRobot style={{ fontSize: '0.9em' }} />
                      {counts.totalPlates} Plate{counts.totalPlates !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Evidence Statistics */}
              {counts.totalEvidence > 0 && (
                <div style={{ 
                  display: 'flex', 
                  gap: '15px', 
                  marginBottom: '10px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#ccc',
                    fontSize: '0.9em'
                  }}>
                    <FaVideo style={{ color: '#76a9ea' }} />
                    <span>{counts.totalEvidence} evidence file{counts.totalEvidence !== 1 ? 's' : ''}</span>
                  </div>
                  
                  {counts.analyzedEvidence > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#ccc',
                      fontSize: '0.9em'
                    }}>
                      <FaRobot style={{ color: '#4CAF50' }} />
                      <span>{counts.analyzedEvidence} analyzed</span>
                    </div>
                  )}
                  
                  {counts.totalVehicles > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#ccc',
                      fontSize: '0.9em'
                    }}>
                      <FaEye style={{ color: '#FF9800' }} />
                      <span>{counts.totalVehicles} vehicle{counts.totalVehicles !== 1 ? 's' : ''} detected</span>
                    </div>
                  )}
                  
                  {counts.recentActivity > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: '#4CAF50',
                      fontSize: '0.9em'
                    }}>
                      <FaClock />
                      <span>{counts.recentActivity} new this week</span>
                    </div>
                  )}
                </div>
              )}
            
              {/* Show description excerpt for search results */}
              {searchTerm && file.description && (
                <p style={{ 
                  marginTop: '8px', 
                  marginBottom: '8px', 
                  color: '#ccc', 
                  fontSize: '0.9em',
                  lineHeight: '1.4'
                }}>
                  {file.description.length > 150 
                    ? `${file.description.substring(0, 150)}...` 
                    : file.description
                  }
                </p>
              )}
            
              {/* Show categories if they exist */}
              {file.categories && file.categories.length > 0 && (
                <div style={{ marginTop: '8px', marginBottom: '5px' }}>
                  {file.categories.map((category) => (
                    <span 
                      key={category} 
                      onClick={() => handleCategoryFilter(category)}
                      style={{
                        display: 'inline-block',
                        backgroundColor: selectedCategory === category ? '#E53935' : '#333',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                        marginRight: '5px',
                        marginBottom: '3px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedCategory !== category) {
                          e.target.style.backgroundColor = '#444';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedCategory !== category) {
                          e.target.style.backgroundColor = '#333';
                        }
                      }}
                      title={`Filter by ${category}`}
                    >
                      {category}
                    </span>
                  ))}
                </div>
              )}
              {isExpanded && (
                <div style={{
                  marginTop: '10px',
                  padding: '15px',
                  borderTop: '1px solid #333',
                  backgroundColor: '#151515',
                  borderRadius: '6px'
                }}>
                  {/* Quick Stats Section */}
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#76a9ea', marginBottom: '10px' }}>
                      <FaChartBar />
                      <span style={{ fontWeight: 600 }}>Quick Stats</span>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                      gap: '10px',
                      marginBottom: '10px'
                    }}>
                      {/* Evidence Count */}
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#1a2332',
                        borderRadius: '6px',
                        border: '1px solid #2a3441',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: '#76a9ea', fontSize: '1.2em', fontWeight: 'bold' }}>
                          {counts.totalEvidence || 0}
                        </div>
                        <div style={{ color: '#ccc', fontSize: '0.8em' }}>Evidence Files</div>
                      </div>

                      {/* Links */}
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#1a2d1a',
                        borderRadius: '6px',
                        border: '1px solid #2a4d2a',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: '#4CAF50', fontSize: '1.2em', fontWeight: 'bold' }}>
                          {counts.analyzedEvidence || 0}
                        </div>
                        <div style={{ color: '#ccc', fontSize: '0.8em' }}>Links</div>
                      </div>

                      {/* Orders */}
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#2d1a1a',
                        borderRadius: '6px',
                        border: '1px solid #4d2a2a',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: '#FF9800', fontSize: '1.2em', fontWeight: 'bold' }}>
                          {counts.totalVehicles || 0}
                        </div>
                        <div style={{ color: '#ccc', fontSize: '0.8em' }}>Orders</div>
                      </div>

                      {/* Jobs */}
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#1a1a2d',
                        borderRadius: '6px',
                        border: '1px solid #2a2a4d',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: '#9C27B0', fontSize: '1.2em', fontWeight: 'bold' }}>
                          {counts.totalPlates || 0}
                        </div>
                        <div style={{ color: '#ccc', fontSize: '0.8em' }}>Jobs</div>
                      </div>
                    </div>

                    {/* Analysis Progress Bar */}
                    {counts.totalEvidence > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <span style={{ color: '#ccc', fontSize: '0.85em' }}>Analysis Progress</span>
                          <span style={{ color: '#4CAF50', fontSize: '0.85em' }}>
                            {counts.analyzedEvidence}/{counts.totalEvidence} files
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          backgroundColor: '#333',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${counts.totalEvidence > 0 ? (counts.analyzedEvidence / counts.totalEvidence) * 100 : 0}%`,
                            height: '100%',
                            backgroundColor: '#4CAF50',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {file.description && (
                    <div style={{ marginBottom: '15px' }}>
                      <div style={{ color: '#ccc', fontSize: '0.9em', fontWeight: 600, marginBottom: '6px' }}>
                        Case Description
                      </div>
                      <div style={{ color: '#bbb', fontSize: '0.9em', lineHeight: '1.5', fontStyle: 'italic' }}>
                        "{file.description}"
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    marginBottom: '15px',
                    flexWrap: 'wrap'
                  }}>
                    <Link 
                      to={`/casefile/${file.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        backgroundColor: '#E53935',
                        color: '#fff',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '0.85em',
                        fontWeight: '500',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#E53935'}
                    >
                      <FaPlay style={{ fontSize: '0.8em' }} />
                      Open Case
                    </Link>
                    
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: file.title,
                            text: `Check out this public case: ${file.title}`,
                            url: `${window.location.origin}/casefile/${file.id}`
                          });
                        } else {
                          navigator.clipboard.writeText(`${window.location.origin}/casefile/${file.id}`);
                          // Could add a toast notification here
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        backgroundColor: 'transparent',
                        color: '#76a9ea',
                        border: '1px solid #76a9ea',
                        borderRadius: '6px',
                        fontSize: '0.85em',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#76a9ea';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#76a9ea';
                      }}
                    >
                      <FaShareAlt style={{ fontSize: '0.8em' }} />
                      Share
                    </button>
                  </div>


                </div>
              )}

              <p style={{ marginTop: '5px', color: '#aaa', fontSize: '0.9em' }}>
                Created: {file.createdAt ? new Date(file.createdAt.toDate()).toLocaleString() : 'N/A'}
                {file.createdBy && (
                  <span style={{ marginLeft: '10px' }}>
                    by <span style={{ color: '#76a9ea' }}>{creatorDisplayName}</span>
                  </span>
                )}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PublicCasefiles; 